import React from "react";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { formatBusinessDate, formatDateOnly } from "@helpers/date";
import { TransactionMatch } from "@common/types/transactionMatch";
import { EagerLoaded } from "@common/types/base/orm";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { useShowTransactionDetails } from "@hooks/useShowTransactionDetails";
import { Amount } from "@design-system/Amount/Amount";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useDetachFinancialDocumentMutation } from "@mutations/financialDocument";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ArrowCornerIcon, DetachIcon } from "@icons";
import styles from "./FinancialDocumentMatches.module.scss";

interface Props {
    transactionMatch: EagerLoaded<TransactionMatch, "financialTransaction">;
    financialDocument: FinancialDocumentExtended;
}

export const FinancialTransactionMatch: React.FC<Props> = ({
    financialDocument,
    transactionMatch,
}) => {
    const financialTransaction = transactionMatch.financialTransaction;
    const showTransactionDetails = useShowTransactionDetails();

    const detachMutation = useDetachFinancialDocumentMutation(
        financialDocument,
        transactionMatch,
    );

    return (
        <article
            className={styles.item}
            data-testid="financial-document-transaction-match-item"
        >
            <main>
                <div
                    className="text-gray-700"
                    data-testid="financial-document-transaction-match-item-date"
                >
                    {financialTransaction.dateOnly
                        ? formatDateOnly(
                              financialTransaction.dateOnly,
                              fullDateFullYearFormat,
                          )
                        : formatBusinessDate(
                              financialTransaction.date,
                              fullDateFullYearFormat,
                          )}
                </div>

                <div
                    className={styles.description}
                    data-testid="financial-document-transaction-match-item-description"
                >
                    {financialTransaction.bankDescription}
                </div>
            </main>

            <Amount amount={financialTransaction!.amount} />
            <aside>
                <ButtonWithLoader
                    variant="secondary"
                    size="sm"
                    iconOnly
                    aria-label="Detach from this transaction"
                    onClick={() => detachMutation.mutate()}
                    loading={detachMutation.isPending}
                    tooltip="Detach from this transaction"
                    data-testid="financial-document-transaction-match-detach-button"
                >
                    <DetachIcon />
                </ButtonWithLoader>

                <Tooltip content="View transaction">
                    <Button
                        variant="secondary"
                        size="sm"
                        iconOnly
                        onClick={() =>
                            showTransactionDetails(financialTransaction)
                        }
                        aria-label="View transaction"
                        data-testid="financial-document-transaction-match-view-transaction-button"
                    >
                        <ArrowCornerIcon />
                    </Button>
                </Tooltip>
            </aside>
        </article>
    );
};
